/**
 * UnifiedAuthProvider - Combines wallet and Privy authentication.
 * 
 * This provider:
 * 1. Wraps both wallet and Privy auth providers
 * 2. Provides a unified interface for authentication
 * 3. Determines which auth method is active
 * 4. Provides consistent user ID regardless of auth method
 */

import { createContext, useEffect, type ReactNode } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { useAuth } from "./use-auth";
import { usePrivyAuth } from "./use-privy-auth";
import { debug } from "@/lib/debug";
import { trackLead } from "@/lib/meta-pixel";

export type AuthMethod = "wallet" | "privy" | null;

export interface UnifiedAuthContextValue {
  /** Which authentication method is active */
  authMethod: AuthMethod;
  /** Whether authentication system is ready (not still initializing) */
  isReady: boolean;
  /** Whether user is authenticated (via any method) */
  isAuthenticated: boolean;
  /** User ID (database UUID from backend) */
  userId: string | null;
  /** Wallet address for display and signing */
  walletAddress: string | null;
  /** User's email (only for Privy auth with email) */
  email: string | null;
  /** Login method from database: 'wallet', 'privy', or 'both' (wallet user who linked email) */
  loginMethod: string | null;
  /** Whether user can trade (has valid auth) */
  canTrade: boolean;
  /** Whether user has valid authentication for API/sync operations */
  hasValidAuth: boolean;
  /** Access token for API calls (backend JWT) */
  accessToken: string | null;
  /** Logout from current auth method */
  logout: () => Promise<void>;
}

export const UnifiedAuthContext = createContext<UnifiedAuthContextValue | null>(null);

interface UnifiedAuthProviderProps {
  children: ReactNode;
}

export function UnifiedAuthProvider({ children }: UnifiedAuthProviderProps) {
  const { connected, connecting, publicKey, disconnect, wallet } = useWallet();
  const walletAuth = useAuth();
  const privyAuth = usePrivyAuth();

  // Check if auth systems are ready
  // CRITICAL: Only check the auth system that's actually being used!
  // - Wallet auth: Ready when wallet finishes connecting
  // - Privy auth: Ready when Privy finishes initializing
  // Don't require BOTH to be ready - that breaks wallet auth when Privy loads slowly
  const isWalletReady = !connecting;

  // Determine active auth method
  // Priority: External Wallet > Privy
  const isWalletAuth = connected && publicKey && walletAuth.isAuthenticated;
  const isPrivyAuth = !isWalletAuth && privyAuth.authenticated && privyAuth.solanaWalletAddress;
  
  const authMethod: AuthMethod = isWalletAuth 
    ? "wallet" 
    : isPrivyAuth
      ? "privy"
      : null;

  const isAuthenticated = authMethod !== null;

  // Get isNewUser from whichever auth method is active
  const isNewUser = isWalletAuth 
    ? walletAuth.isNewUser 
    : isPrivyAuth 
      ? privyAuth.isNewUser 
      : false;

  // isReady depends on which auth method is active:
  // - Wallet users: Ready when wallet finishes connecting
  // - Privy users: Ready when Privy finishes initializing  
  // - No auth: Ready when both systems have initialized (so UI doesn't flicker)
  const isReady = isWalletAuth 
    ? isWalletReady 
    : isPrivyAuth
      ? privyAuth.ready
      : (isWalletReady && privyAuth.ready);

  // Get user ID (database UUID) based on auth method
  // Both wallet and Privy auth now return database UUIDs from the backend
  const userId = isWalletAuth 
    ? walletAuth.userId  // Database UUID from wallet auth
    : isPrivyAuth
      ? privyAuth.userId  // Database UUID from Privy auth
      : null;

  // Get wallet address for display and signing operations
  const walletAddress = isWalletAuth 
    ? publicKey?.toBase58() ?? null
    : isPrivyAuth
      ? privyAuth.solanaWalletAddress
      : null;

  // Get email based on auth method:
  // - Wallet users: from linked email stored in backend
  // - Privy users: from Privy user object
  const email = isWalletAuth 
    ? walletAuth.email  // Linked email for wallet users
    : isPrivyAuth && privyAuth.user?.email?.address
      ? privyAuth.user.email.address
      : null;

  // Get login method from database (important for linked accounts)
  // 'wallet' = pure wallet user, 'privy' = pure email user, 'both' = wallet user who linked email
  const loginMethod = isWalletAuth 
    ? walletAuth.loginMethod 
    : isPrivyAuth 
      ? privyAuth.loginMethod
      : null;

  // Can trade if authenticated via any method
  const canTrade = isAuthenticated;

  // Check if we have valid auth for API/sync operations (HTTP)
  // All auth methods use backend JWTs
  const hasValidAuth = isWalletAuth 
    ? !!walletAuth.token  // Wallet users need wallet JWT token
    : isPrivyAuth
      ? !!privyAuth.backendToken  // Privy users need backend JWT from token exchange
      : false;

  // Get access token for WebSocket authentication
  // All auth methods use backend JWTs (same format)
  const accessToken = isWalletAuth 
    ? walletAuth.token  // Wallet JWT from signature auth
    : isPrivyAuth
      ? privyAuth.backendToken  // Backend JWT from Privy token exchange
      : null;

  // Debug logging for wallet auth issues (after all variables are declared)
  useEffect(() => {
    debug.log('UnifiedAuth', `State: connecting=${connecting}, connected=${connected}, isWalletAuth=${isWalletAuth}, isPrivyAuth=${isPrivyAuth}, isReady=${isReady}, userId=${userId?.slice(0,8)}, hasToken=${!!accessToken}`);
  }, [connecting, connected, isWalletAuth, isPrivyAuth, isReady, userId, accessToken]);

  // Track Meta Pixel Lead event ONLY when a brand new user signs up
  // This prevents firing Lead events for returning users who just log in
  useEffect(() => {
    if (isAuthenticated && isReady && userId && isNewUser) {
      // Fire Meta Pixel Lead event with auth details
      trackLead({
        authMethod,
        walletType: isWalletAuth && wallet?.adapter?.name 
          ? wallet.adapter.name 
          : undefined,
        email: email || undefined,
      });
    }
  }, [isAuthenticated, isReady, userId, isNewUser, authMethod, email, isWalletAuth, wallet?.adapter?.name]);

  // Unified logout
  const logout = async () => {
    // Always clear wallet auth token state first
    // Both Privy and wallet auth use the same localStorage for tokens,
    // so we need to clear the React state in useAuthToken (via walletAuth.logout())
    // Otherwise walletAuth.isAuthenticated stays true after Privy logout,
    // which causes the LoginModal to immediately close when opened.
    walletAuth.logout();
    
    if (isWalletAuth) {
      await disconnect();
      // Also logout from Privy to clear any session created during email linking
      // This prevents Privy's embedded wallet from taking over after disconnect
      try {
        await privyAuth.logout();
      } catch {
        // Ignore errors if Privy wasn't logged in - this is expected
      }
    } else if (isPrivyAuth) {
      await privyAuth.logout();
    }
  };

  const value: UnifiedAuthContextValue = {
    authMethod,
    isReady,
    isAuthenticated,
    userId,
    walletAddress,
    email,
    loginMethod,
    canTrade,
    hasValidAuth,
    accessToken,
    logout,
  };

  return (
    <UnifiedAuthContext.Provider value={value}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}
